import { css } from '@emotion/react';
import { noop } from 'lodash';
import { Fragment, memo } from 'react';

import type { Notification, NotificationTypes } from '@didaskill/domain/notifications/types';

import { NotificationListItem, type NotificationListItemProps } from './item/NotificationListItem';
import { NotificationListItemSkeleton } from './item/skeleton/NotificationListItemSkeleton';

export type NotificationsListProps<T extends NotificationTypes> = {
  /** List of notifications to display. This component does not handle the empty state.   */
  notifications: Notification[];
  /** Mapping from notification.notificationType to a component rendering the notification. */
  rendererMapping: NotificationListItemProps<T>['rendererMapping'];
  /** Mark a notification as read. Called with the notification id. */
  onMarkNotificationAsRead?: NotificationListItemProps<T>['onMarkAsRead'];
  /** Mark a notificatrion as unread. Called with the notification id. */
  onMarkNotificationAsUnread?: NotificationListItemProps<T>['onMarkAsUnread'];
  /** Show skeletons if loading. */
  onClose?: NotificationListItemProps<T>['onCloseNotifications'];
  isLoading?: boolean;
  /** Closes the list of notifications. */
  onClickNotification?: NotificationListItemProps<T>['onClick'];
};

const DEFAULT_NOTIFICATIONS = [];

export const NotificationsListBase = <T extends NotificationTypes>({
  notifications = DEFAULT_NOTIFICATIONS,
  rendererMapping,
  onMarkNotificationAsRead = noop,
  onMarkNotificationAsUnread = noop,
  isLoading = false,
  onClose = noop,
  onClickNotification = noop,
}: NotificationsListProps<T>) => (
  <ul
    css={(theme) => css`
      > li {
        list-style-type: none;
        &:not(:first-of-type) {
          border-top: 1px solid ${theme.colors.gray[100]};
        }
      }
    `}
  >
    {notifications.map((notification) => (
      <li key={notification.id}>
        <NotificationListItem<T>
          notification={notification}
          rendererMapping={rendererMapping}
          onMarkAsRead={onMarkNotificationAsRead}
          onMarkAsUnread={onMarkNotificationAsUnread}
          onCloseNotifications={onClose}
          onClick={onClickNotification}
        />
      </li>
    ))}

    {!!isLoading && (
      <Fragment>
        <li>
          <NotificationListItemSkeleton />
        </li>
        <li>
          <NotificationListItemSkeleton />
        </li>
      </Fragment>
    )}
  </ul>
);

NotificationsListBase.displayName = 'NotificationsList';

export const NotificationsList = memo(NotificationsListBase) as typeof NotificationsListBase;
